import { Injectable } from '@angular/core';
import { CONSTANTS } from '../../Constants/Constants';
import { SellPriceCalculationResult, SellPriceConfig } from '../../models/sol-estimate-table/SellPriceConfig';
import { EstimateTab } from '../../models/dtos';
import { EstimateCommonFunctionsService } from './estimate-common-functions.service';
import {
  EstimateUiModel,
  EstimateRowExtendedModel,
  createExtendedRowModel,
} from '../../models/sol-estimate-table/ui-models/index';
import { EstimateLastModifiedSellPriceService } from './estimate-last-modified-sell-price.service';
import { EstimateRowExtendedModelSubContractor } from '../../models/sol-estimate-table/ui-models/estimate-row.model';

/**
 * Handles all subcontractor-costs-related logic for the estimate tab.
 * Works exclusively with EstimateUiModel — no FormGroup references.
 */
@Injectable({
  providedIn: 'root'
})
export class SolEstimateSubcontractorService {

  constructor(
    private readonly estimateCommonFunctions: EstimateCommonFunctionsService,
    private readonly lastModifiedSellPriceService: EstimateLastModifiedSellPriceService
  ) { }

  // ── Patching (initial load) ────────────────────────────────────

  // TODO: Subcontractor costs still sourced from EstimateTab.estimateTabSubContractors (line-item array).
  // SQWebProject only has subcontractorCategory.sumUpCost (single total) with no equivalent line-item breakdown.
  // Migrate to SQWebProject once an equivalent array is available.
  patchSubContractorCosts(
    model: EstimateUiModel,
    estimateValues: EstimateTab,
    config: SellPriceConfig,
    subTotalsCost: number
  ): void {

    // Clear existing rows and populate from DTO, merging rows with the same referenceNumber
    model.subContractorCosts.subContractorsCostsRow = [];

    const dtoSubContractors = estimateValues.estimateTabSubContractors ?? [];

    for (const sub of dtoSubContractors) {
      const refNo = sub.referenceNumber ?? '';
      const desc = sub.description ?? refNo;
      const cost = sub.cost ?? CONSTANTS.ZERO;

      const row = createExtendedRowModel(desc, sub.isLastModifiedSubContSellPrice, cost);
      const gmPercent = sub.gmPercent ?? null;
      if (gmPercent != null) {
        // Stored percent is in the active unit (MU% when markup is on); derive both.
        const pair = this.estimateCommonFunctions.resolvePercentPair(gmPercent, config.isMarkUpSelected);
        row.grossMarginPercentage = pair.grossMarginPercentage;
        row.markUpPercentage = pair.markUpPercentage;
      }
      row.sellPrice = sub.sellPrice ?? CONSTANTS.ZERO;
      model.subContractorCosts.subContractorsCostsRow.push(row);
    }

    const { totalCost, totalSellPrice } = this.calculateAndUpdateRows(
      model.subContractorCosts.subContractorsCostsRow, config, subTotalsCost
    );

    this.estimateCommonFunctions.updateSectionTotalsHeader(
      model.subContractorCosts.headerRow,
      totalCost, totalSellPrice, subTotalsCost, config
    );
  }

  // ── Recalculation ──────────────────────────────────────────────

  recalculateSubContractorSellPrices(model: EstimateUiModel, config: SellPriceConfig): void {
    const isMarkUp = config.isMarkUpSelected;
    const stc = model.subTotals.cost;
    const rows = model.subContractorCosts.subContractorsCostsRow;

    const { totalCost, totalSellPrice } = this.calculateAndUpdateRows(rows, config, stc);

    this.estimateCommonFunctions.updateSectionTotalsHeader(
      model.subContractorCosts.headerRow,
      totalCost, totalSellPrice, stc, config
    );
  }

  // ── Totals Recalculation ───────────────────────────────────────

  recalculateSubContractorTotals(model: EstimateUiModel, config: SellPriceConfig): void {
    const rows = model.subContractorCosts.subContractorsCostsRow;
    let totalCost = CONSTANTS.ZERO;
    let totalSellPrice = CONSTANTS.ZERO;

    for (const row of rows) {
      totalCost += row.cost;
      totalSellPrice += row.sellPrice;
    }

    this.estimateCommonFunctions.updateSectionTotalsHeader(
      model.subContractorCosts.headerRow,
      totalCost, totalSellPrice, model.subTotals.cost, config
    );
  }

  // ── Reactive Handler ───────────────────────────────────────────

  onSubContractorRowPercentChanged(
    model: EstimateUiModel,
    config: SellPriceConfig,
    rowIndex: number,
    newPercent: number,
    isGMPercent: boolean
  ): void {
    const isMarkUp = !isGMPercent;
    const row = model.subContractorCosts.subContractorsCostsRow[rowIndex];
    const cost = row.cost;
    if (config.isFixedPriceSelected === null) {
      row.isSellPriceLastModified = false;
    }
    const result = this.estimateCommonFunctions.calculateRowSellPrice(cost, newPercent, isMarkUp);

    this.estimateCommonFunctions.updateRowResult(row, result, model.subTotals.cost);
    this.recalculateSubContractorTotals(model, config);
  }

  /**
   * Called when user edits a subcontractor row's sell price directly (Line Item mode only).
   * Derives GM%, MU%, and TDC% from the new sell price and row cost, then
   * recalculates subcontractor section totals.
   */
  onSubContractorRowSellPriceChanged(
    model: EstimateUiModel,
    rowIndex: number,
    newSellPrice: number,
    config: SellPriceConfig
  ): void {
    const row = model.subContractorCosts.subContractorsCostsRow[rowIndex];
    if (!row) {
      return;
    }

    if (config.isFixedPriceSelected === null) {
      row.isSellPriceLastModified = true;
    }
    const result = this.estimateCommonFunctions.recomputePercentsFromSellPrice(row.cost, newSellPrice);
    this.estimateCommonFunctions.updateRowResult(row, result, model.subTotals.cost);
    this.recalculateSubContractorTotals(model, config);
  }

  /** Clears the sell-price-driven flag so every row is derived from margin again. */
  resetSellPriceLastModifiedFlags(model: EstimateUiModel): void {
    for (const row of model.subContractorCosts.subContractorsCostsRow) {
      row.isSellPriceLastModified = false;
    }
  }

  // ── Refresh Displayed Percentages (GM↔MU toggle) ──────────────

  // ── Private Helper ─────────────────────────────────────────────

  private calculateAndUpdateRows(
    rows: EstimateRowExtendedModelSubContractor[],
    config: SellPriceConfig,
    subTotalsCost: number
  ): { totalCost: number; totalSellPrice: number } {
    let totalCost = CONSTANTS.ZERO;
    let totalSellPrice = CONSTANTS.ZERO;

    for (const row of rows) {
      const result = config.isFixedPriceSelected !== null
        ? this.computeFixedPriceResult(row, config)
        : this.computeLineItemResult(row, config);
      this.estimateCommonFunctions.updateRowResult(row, result, subTotalsCost);
      totalCost += row.cost;
      totalSellPrice += result.sellPrice;
    }

    return { totalCost, totalSellPrice };
  }

  private computeFixedPriceResult(
    row: EstimateRowExtendedModelSubContractor,
    config: SellPriceConfig
  ): SellPriceCalculationResult {
    const presentPercent = row.grossMarginPercentage;
    const gmPercent = this.estimateCommonFunctions.resolveRowGMPercent(
      config, Number.isNaN(presentPercent) ? undefined : presentPercent
    );
    const preservedSellPrice = this.estimateCommonFunctions.resolvePreservedSellPrice(
      row, config, row.isSellPriceLastModified
    );
    return this.estimateCommonFunctions.calculateRowSellPrice(
      row.cost, gmPercent, config.isMarkUpSelected, preservedSellPrice
    );
  }

  private computeLineItemResult(
    row: EstimateRowExtendedModelSubContractor,
    config: SellPriceConfig
  ): SellPriceCalculationResult {
    if (row.isSellPriceLastModified) {
      return this.estimateCommonFunctions.recomputePercentsFromSellPrice(row.cost, row.sellPrice);
    }
    // Row GM% is always true GM%; convert to MU% when markup is on.
    const presentPercent = row.grossMarginPercentage;

    const percent = this.estimateCommonFunctions.resolveRowGMPercent(
      config, Number.isNaN(presentPercent) ? undefined : presentPercent
    );
    return this.estimateCommonFunctions.calculateRowSellPrice(row.cost, percent, config.isMarkUpSelected);
  }

  refreshSubContractorPercentages(model: EstimateUiModel): void {
    for (const row of model.subContractorCosts.subContractorsCostsRow) {
      this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(row);
    }
    this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(model.subContractorCosts.headerRow);
  }
}
